import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Crown, MessageCircle, Star, Users, Flag, Ban, Loader2, UserPlus, UserCheck } from 'lucide-react';
import { CharacterCard } from '../components/CharacterCard';
import { ReportModal, BlockButton } from '../components/ReportModal';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { GemIcon } from '../components/GemIcon';
import UltimateTag from '../components/UltimateTag';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  0: '#71717A', 1: '#A1A1AA', 2: '#60A5FA', 3: '#34D399', 4: '#FBBF24',
  5: '#F59E0B', 6: '#F97316', 7: '#EF4444', 8: '#E11D48', 9: '#A855F7', 10: '#E11D48',
};

export default function PublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [giftAmount, setGiftAmount] = useState('');
  const [gifting, setGifting] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
    if (user && user.id !== userId) {
      checkFollowStatus();
    }
  }, [userId, user]);

  const checkFollowStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/${userId}/follow-status`, { withCredentials: true });
      setIsFollowing(data.is_following);
    } catch (err) {
      console.error('Follow status check failed:', err);
    }
  };

  const toggleFollow = async () => {
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.post(`${API_URL}/api/users/${userId}/unfollow`, {}, { withCredentials: true });
        setIsFollowing(false);
        if (profile) setProfile(p => ({ ...p, followers_count: (p.followers_count || 1) - 1 }));
      } else {
        await axios.post(`${API_URL}/api/users/${userId}/follow`, {}, { withCredentials: true });
        setIsFollowing(true);
        if (profile) setProfile(p => ({ ...p, followers_count: (p.followers_count || 0) + 1 }));
      }
    } catch (err) {
      console.error('Follow error:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/${userId}/public`);
      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendGift = async () => {
    const amount = parseInt(giftAmount);
    if (!amount || amount <= 0) return;
    setGifting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/gems/gift`, {
        recipient_id: userId,
        amount
      }, { withCredentials: true });
      alert(data.message);
      setShowGift(false);
      setGiftAmount('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send gems');
    } finally {
      setGifting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-3">
        <Users className="w-12 h-12 text-[#71717A]" />
        <p className="text-[#71717A]">User not found</p>
        <button onClick={() => navigate(-1)} className="text-[#E11D48] text-sm">Go back</button>
      </div>
    );
  }

  const levelColor = LEVEL_COLORS[profile.level] || '#71717A';

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="sticky top-12 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white" data-testid="public-profile-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white text-sm">{profile.name}</h1>
      </div>

      {/* Profile Card */}
      <div className="px-4 pt-5">
        <div className="flex items-start gap-4">
          <div className="w-20 h-20 rounded-full bg-[#1A1D24] border-2 border-white/10 overflow-hidden flex-shrink-0">
            {profile.avatar ? (
              <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Users className="w-8 h-8 text-[#71717A]" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <h2 className="text-xl font-bold text-white font-['Unbounded'] flex items-center gap-2 flex-wrap" data-testid="public-profile-name">
              {profile.name}
              {profile.ultimate_tag && <UltimateTag tag={profile.ultimate_tag} />}
              {profile.badge && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#E11D48] text-white rounded-full" data-testid="public-profile-badge">
                  <Crown className="w-3 h-3" /> {profile.badge}
                </span>
              )}
            </h2>
            {profile.bio && (
              <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">{profile.bio}</p>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {profile.level > 0 && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium border"
                  style={{ color: levelColor, borderColor: `${levelColor}40`, background: `${levelColor}10` }}
                  data-testid="public-profile-level"
                >
                  Lv.{profile.level} {profile.level_name}
                </span>
              )}
              <span className="text-xs text-[#71717A] flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> {profile.total_chats.toLocaleString()} chats
              </span>
              <span className="text-xs text-[#71717A] flex items-center gap-1">
                <Users className="w-3 h-3" /> {profile.character_count} characters
              </span>
            </div>
            {/* Follower/Following counts */}
            <div className="flex items-center gap-4 mt-3">
              <div className="text-center">
                <p className="text-white font-bold text-sm">{(profile.followers_count || 0).toLocaleString()}</p>
                <p className="text-[10px] text-[#71717A]">Followers</p>
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-sm">{(profile.following_count || 0).toLocaleString()}</p>
                <p className="text-[10px] text-[#71717A]">Following</p>
              </div>
            </div>
          </div>
        </div>
        {/* Actions (only show for other users) */}
        {user && user.id !== userId && (
          <div className="mt-3 space-y-2">
            {/* Follow Button */}
            <button
              onClick={toggleFollow}
              disabled={followLoading}
              className={`w-full p-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-all active:scale-[0.98] ${
                isFollowing
                  ? 'bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-[#E11D48]/10 hover:text-[#E11D48] hover:border-[#E11D48]/20'
                  : 'bg-[#E11D48] text-white hover:bg-[#BE123C]'
              }`}
              data-testid="follow-btn"
            >
              {followLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isFollowing ? (
                <><UserCheck className="w-4 h-4" /> Following</>
              ) : (
                <><UserPlus className="w-4 h-4" /> Follow</>
              )}
            </button>

            {/* Gift Gems */}
            {!showGift ? (
              <button
                onClick={() => setShowGift(true)}
                className="w-full p-3 bg-gradient-to-r from-[#F59E0B]/10 to-[#E11D48]/10 border border-[#F59E0B]/20 rounded-xl flex items-center gap-3 hover:from-[#F59E0B]/20 hover:to-[#E11D48]/20 transition-all active:scale-[0.98]"
                data-testid="gift-gems-btn"
              >
                <GemIcon size={20} />
                <span className="text-sm font-medium text-white">Gift Gems to {profile.name}</span>
              </button>
            ) : (
              <div className="p-3 bg-[#1A1D24] border border-[#F59E0B]/20 rounded-xl space-y-2">
                <p className="text-xs text-[#A1A1AA]">How many gems to gift?</p>
                <div className="flex gap-2">
                  {[100, 500, 1000, 5000].map(amt => (
                    <button
                      key={amt}
                      onClick={() => setGiftAmount(String(amt))}
                      className={`flex-1 py-1.5 text-xs rounded-full border transition-colors ${
                        giftAmount === String(amt)
                          ? 'bg-[#F59E0B]/20 border-[#F59E0B]/50 text-[#F59E0B]'
                          : 'bg-white/5 border-white/10 text-[#A1A1AA] hover:bg-white/10'
                      }`}
                      data-testid={`gift-preset-${amt}`}
                    >
                      {amt.toLocaleString()}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(e.target.value)}
                  placeholder="Custom amount"
                  min="1"
                  className="w-full bg-[#0F1115] text-white text-sm rounded-lg px-3 py-2 border border-white/10 focus:border-[#F59E0B]/50 focus:outline-none placeholder:text-[#555]"
                  data-testid="gift-amount-input"
                />
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowGift(false); setGiftAmount(''); }}
                    className="flex-1 text-[#71717A] hover:text-white rounded-full text-xs"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={sendGift}
                    disabled={!giftAmount || parseInt(giftAmount) <= 0 || gifting}
                    className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-black rounded-full text-xs"
                    data-testid="send-gift-btn"
                  >
                    {gifting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <GemIcon size={12} />}
                    {gifting ? 'Sending...' : 'Send Gems'}
                  </Button>
                </div>
              </div>
            )}
            {/* Report & Block */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReport(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#71717A] bg-white/5 border border-white/5 rounded-full hover:bg-white/10 transition-colors"
                data-testid="report-user-btn"
              >
                <Flag className="w-3 h-3" /> Report
              </button>
              <BlockButton userId={userId} userName={profile.name} />
            </div>
          </div>
        )}
      </div>

      {/* Characters */}
      <div className="px-4 mt-6">
        <h3 className="text-sm font-bold text-white mb-3" data-testid="public-profile-chars-heading">
          Characters by {profile.name}
        </h3>
        {profile.characters.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {profile.characters.map((char) => (
              <CharacterCard key={char.id} character={char} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-[#71717A]">No public characters yet</p>
          </div>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        targetType="user"
        targetId={userId}
        targetName={profile?.name}
      />
    </div>
  );
}
